// IntegrityConnectCard — Settings ▸ Integrations panel that lets admins
// add, rotate, probe, disable, and delete Integrity Connect partner
// credentials. One row per (agent, NPN) pair. Secrets are write-only —
// list responses return a masked clientId and never the secret.
//
// Roles: this whole card is admin+/superadmin only. Agents don't see it.
//
// UI contracts:
//   - Add / Rotate: POST /api/integrity/credentials
//       { agentId, npn, clientId, clientSecret, label? }
//   - List:        GET /api/integrity/credentials
//   - Probe:       POST /api/integrity/credentials/:id/probe
//   - Disable:     POST /api/integrity/credentials/:id/disable
//   - Delete:      DELETE /api/integrity/credentials/:id

import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Link2, KeyRound, PlusCircle, Loader2, ShieldCheck, ShieldAlert,
  Trash2, Play, PowerOff,
} from 'lucide-react';
import { apiGet, apiPost, api } from '../../lib/api.js';

export default function IntegrityConnectCard({ currentAgent }) {
  const qc = useQueryClient();

  // /api/me does not exist — the real profile endpoint is /api/auth/me
  // and it returns { ok, agent: { role, ... } }. Old code hit /me,
  // silently 404'd, meQ.data was null, isAdmin was false, and the
  // whole card rendered null on Settings ▸ Integrations. Fix: hit
  // /auth/me and read the nested agent.
  const meQ = useQuery({
    queryKey: ['integrity-me'],
    queryFn: () => apiGet('/auth/me').catch(() => null),
    staleTime: 60_000,
  });
  const meAgent = meQ.data?.agent || meQ.data || null;
  const roleStr = (meAgent?.role || currentAgent?.role || '').toString().toLowerCase();
  const isAdmin = ['admin', 'superadmin'].includes(roleStr);

  const credsQ = useQuery({
    queryKey: ['integrity-credentials'],
    queryFn: () => apiGet('/integrity/credentials'),
    enabled: isAdmin,
  });
  const agentsQ = useQuery({
    queryKey: ['integrity-agents'],
    queryFn: () => apiGet('/agents'),
    enabled: isAdmin,
  });

  const [form, setForm] = useState({
    agentId: '',
    npn: '',
    clientId: '',
    clientSecret: '',
    label: '',
  });

  const addMut = useMutation({
    mutationFn: () => apiPost('/integrity/credentials', form),
    onSuccess: () => {
      toast.success('Credential saved');
      setForm({ agentId: '', npn: '', clientId: '', clientSecret: '', label: '' });
      qc.invalidateQueries({ queryKey: ['integrity-credentials'] });
    },
    onError: (err) => toast.error(err.message || 'Failed to save credential'),
  });

  const probeMut = useMutation({
    mutationFn: (id) => apiPost(`/integrity/credentials/${id}/probe`),
    onSuccess: (data) => {
      if (data?.ok) toast.success(`Probe OK (${data.status || 200}, ${data.tookMs || 0} ms)`);
      else toast.error(`Probe failed (${data?.status || '?'}): ${data?.error || 'unknown'}`);
    },
    onError: (err) => toast.error(err.message || 'Probe failed'),
    onSettled: () => qc.invalidateQueries({ queryKey: ['integrity-credentials'] }),
  });

  const disableMut = useMutation({
    mutationFn: ({ id, reason }) => apiPost(`/integrity/credentials/${id}/disable`, { reason }),
    onSuccess: () => {
      toast.success('Credential disabled');
      qc.invalidateQueries({ queryKey: ['integrity-credentials'] });
    },
    onError: (err) => toast.error(err.message || 'Disable failed'),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/integrity/credentials/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success('Credential deleted');
      qc.invalidateQueries({ queryKey: ['integrity-credentials'] });
    },
    onError: (err) => toast.error(err.message || 'Delete failed'),
  });

  const credentials = credsQ.data?.credentials || [];
  const agents = agentsQ.data?.agents || agentsQ.data || [];
  const agentsList = useMemo(() => (Array.isArray(agents) ? agents : []), [agents]);

  if (!isAdmin) return null;

  return (
    <div className="rounded-lg bg-civix-panel border border-civix-border p-4 mb-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-white/90 mb-1">
        <Link2 className="w-4 h-4 text-civix-purpleSoft" /> Integrity Connect
      </div>
      <div className="text-[11px] text-white/55 mb-3">
        OAuth partner credentials for pushing leads and quotes into Integrity's Partner Gateway. One row per (agent, NPN). Secrets are stored encrypted and never returned.
      </div>

      {/* Add / rotate form. Rotation uses the same POST — server upserts on
          (tenant, agent, npn, label). Fill agent + NPN + client_id + secret
          from Integrity's Partner Portal. */}
      <div className="rounded-sm bg-civix-bg border border-civix-border p-3 mb-3 space-y-2">
        <div className="text-[11px] font-semibold text-white/75 flex items-center gap-1.5">
          <PlusCircle className="w-3.5 h-3.5" /> Add or rotate a credential
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <select
            value={form.agentId}
            onChange={(e) => setForm({ ...form, agentId: e.target.value })}
            className="input text-xs"
          >
            <option value="">Select agent…</option>
            {agentsList.map((a) => (
              <option key={a._id || a.id} value={a._id || a.id}>{a.name} ({a.email})</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="NPN (e.g. 19071030)"
            value={form.npn}
            onChange={(e) => setForm({ ...form, npn: e.target.value.replace(/\D+/g, '') })}
            className="input text-xs"
          />
          <input
            type="text"
            placeholder="Client ID"
            value={form.clientId}
            onChange={(e) => setForm({ ...form, clientId: e.target.value })}
            className="input text-xs"
            autoComplete="off"
          />
          <input
            type="password"
            placeholder="Client Secret"
            value={form.clientSecret}
            onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
            className="input text-xs"
            autoComplete="new-password"
          />
          <input
            type="text"
            placeholder="Label (optional — e.g. 'primary')"
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            className="input text-xs md:col-span-2"
          />
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => addMut.mutate()}
            disabled={!form.agentId || !form.npn || !form.clientId || !form.clientSecret || addMut.isPending}
            className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
          >
            {addMut.isPending ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</> : <><KeyRound className="w-3 h-3" /> Save credential</>}
          </button>
        </div>
      </div>

      {/* Existing rows. */}
      {credsQ.isLoading && (
        <div className="text-[11px] text-white/55 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
      )}
      {!credsQ.isLoading && credentials.length === 0 && (
        <div className="text-[11px] text-white/45">No credentials yet.</div>
      )}
      {credentials.length > 0 && (
        <div className="divide-y divide-civix-border/60 rounded-sm border border-civix-border/60 bg-civix-bg">
          {credentials.map((c) => (
            <div key={c._id} className="flex items-center gap-3 p-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-white/85 font-medium">NPN {c.npn}</span>
                  {c.label ? <span className="text-white/55">· {c.label}</span> : null}
                  {c.disabled ? (
                    <span className="inline-flex items-center gap-1 rounded-sm bg-red-950 border border-red-800/50 text-red-200 text-[10px] px-1.5 py-0.5">
                      <ShieldAlert className="w-3 h-3" /> Disabled
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-sm bg-emerald-950 border border-emerald-800/50 text-emerald-200 text-[10px] px-1.5 py-0.5">
                      <ShieldCheck className="w-3 h-3" /> Active
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-white/45 mt-0.5">
                  clientId {c.clientIdMasked} · agent {c.agent?.name || c.agent?.email || c.agentId}
                </div>
                {c.lastProbeAt && (
                  <div className="text-[10px] text-white/45 mt-0.5">
                    last probe {new Date(c.lastProbeAt).toLocaleString()} ({c.lastProbeStatus || 'n/a'})
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => probeMut.mutate(c._id)}
                  disabled={probeMut.isPending}
                  className="btn-ghost text-[10px] flex items-center gap-1 px-2 py-1"
                  title="Probe — issues a live token + GET /leads?pageSize=1"
                >
                  <Play className="w-3 h-3" /> Probe
                </button>
                {!c.disabled && (
                  <button
                    type="button"
                    onClick={() => {
                      const reason = window.prompt('Reason for disabling this credential?', '');
                      if (reason == null) return;
                      disableMut.mutate({ id: c._id, reason });
                    }}
                    className="btn-ghost text-[10px] flex items-center gap-1 px-2 py-1"
                  >
                    <PowerOff className="w-3 h-3" /> Disable
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (!window.confirm(`Delete Integrity credential for NPN ${c.npn}? This cannot be undone.`)) return;
                    deleteMut.mutate(c._id);
                  }}
                  className="btn-ghost text-[10px] flex items-center gap-1 px-2 py-1 text-red-300 hover:text-red-200"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
